import { useState } from 'react';

import { faTooth } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faSparkles } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import {
  faCheck,
  faCheckCircle,
  faChevronDown,
  faChevronUp,
  faExclamationTriangle,
  faFileMedical,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@repo/videa-components/src/index';
import { useAtomValue } from 'jotai';

import { StatusChip } from '@/common-components/elements/StatusChip/StatusChip';
import { useClaimAttachmentDisclaimer } from '@/modules/claims/freeClaims/insuranceExport/constants';
import {
  claimNotesAtom,
  claimWarningsAtom,
} from '@/modules/claims/freeClaims/state';

const SHARED_LIST_CLASSES = 'vai-ml-8 vai-mt-2';
const SHARED_ICON_CLASSES = 'vai-text-slate-600 vai-mr-2';
const SHARED_HEADING_CLASSES =
  'vai-flex vai-items-center vai-gap-2 vai-font-semibold';

export const SubmissionChecklist = () => {
  const claimNotes = useAtomValue(claimNotesAtom);
  const claimWarnings = useAtomValue(claimWarningsAtom);
  const [isWarningsOpen, setIsWarningsOpen] = useState(false);
  const [isNotesOpen, setIsNotesOpen] = useState(false);

  const { t } = useLingui();
  const claimAttachmentDisclaimer = useClaimAttachmentDisclaimer();

  const DEFAULT_COMPLETED_ATTACHMENT_NOTES = [
    t`Selected images with diagnosis`,
    t`Generated narrative`,
    t`Documented findings`,
  ];

  const hasNotesOrWarnings = claimNotes.length > 0 || claimWarnings.length > 0;

  return (
    <div>
      <div className="vai-flex vai-items-center vai-justify-between vai-gap-2">
        <h2 className="vai-font-semibold vai-text-lg">
          <Trans>Submission Checklist</Trans>
        </h2>
        {hasNotesOrWarnings ? (
          <StatusChip
            text={<Trans>Needs Review</Trans>}
            variant="warning"
            icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
          />
        ) : (
          <StatusChip
            text={<Trans>Ready to Submit</Trans>}
            variant="success"
            icon={<FontAwesomeIcon icon={faCheckCircle} />}
          />
        )}
      </div>
      <div className="vai-mt-4 first:vai-mt-0">
        {/* Warnings section, show if there ARE warnings */}
        {claimWarnings.length > 0 && (
          <Collapsible
            open={isWarningsOpen}
            onOpenChange={setIsWarningsOpen}
            className="vai-bg-yellow-50 vai-mt-4 vai-p-4 vai-rounded-md"
          >
            <CollapsibleTrigger className="vai-w-full">
              <h3
                className={`${SHARED_HEADING_CLASSES} vai-text-yellow-600 vai-justify-between`}
              >
                <div className="vai-flex vai-items-center vai-gap-2">
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className="vai-text-yellow-500 vai-mr-1"
                  />
                  <Trans>Warnings</Trans>
                </div>
                <div className="vai-flex vai-items-center vai-gap-2">
                  <span className="vai-flex vai-items-center vai-justify-center vai-min-w-[20px] vai-h-5 vai-bg-orange-500 vai-text-white vai-rounded-full vai-text-xs vai-font-semibold vai-px-1.5">
                    {claimWarnings.length}
                  </span>
                  <FontAwesomeIcon
                    icon={isWarningsOpen ? faChevronUp : faChevronDown}
                    className="vai-text-yellow-600"
                  />
                </div>
              </h3>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul className={SHARED_LIST_CLASSES}>
                {claimWarnings.map((warning, index) => (
                  <li key={index} className="vai-mt-1">
                    <FontAwesomeIcon
                      icon={faTooth}
                      className={SHARED_ICON_CLASSES}
                    />
                    {warning}
                  </li>
                ))}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        )}
        {/* Blue section, only show if there ARE notes */}
        {claimNotes.length > 0 ? (
          <Collapsible
            open={isNotesOpen}
            onOpenChange={setIsNotesOpen}
            className="vai-bg-blue-50 vai-mt-4 vai-p-4 vai-rounded-md"
          >
            <CollapsibleTrigger className="vai-w-full">
              <h3
                className={`${SHARED_HEADING_CLASSES} vai-text-blue-600 vai-justify-between`}
              >
                <div className="vai-flex vai-items-center vai-gap-2">
                  <FontAwesomeIcon
                    icon={faFileMedical}
                    className="vai-text-blue-500 vai-mr-1"
                  />
                  <Trans>Strengthen Your Claim</Trans>
                </div>
                <div className="vai-flex vai-items-center vai-gap-2">
                  <span className="vai-flex vai-items-center vai-justify-center vai-min-w-[20px] vai-h-5 vai-bg-blue-500 vai-text-white vai-rounded-full vai-text-xs vai-font-semibold vai-px-1.5">
                    {claimNotes.length}
                  </span>
                  <FontAwesomeIcon
                    icon={isNotesOpen ? faChevronUp : faChevronDown}
                    className="vai-text-blue-600"
                  />
                </div>
              </h3>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul className={SHARED_LIST_CLASSES}>
                {claimNotes.map((note, index) => (
                  <li key={index} className="vai-mt-1">
                    <FontAwesomeIcon
                      icon={faTooth}
                      className={SHARED_ICON_CLASSES}
                    />
                    {note}
                  </li>
                ))}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        ) : (
          // Static green section, show if there are no notes or warnings
          !hasNotesOrWarnings && (
            <div className="vai-bg-green-50 vai-mt-4 vai-p-4 vai-rounded-md">
              <h3 className={`${SHARED_HEADING_CLASSES} vai-text-green-600`}>
                <FontAwesomeIcon
                  icon={faSparkles}
                  className="vai-text-green-600 vai-mr-1"
                />
                <Trans>This AI-generated attachment includes:</Trans>
              </h3>
              <ul className={SHARED_LIST_CLASSES}>
                {DEFAULT_COMPLETED_ATTACHMENT_NOTES.map((note, index) => (
                  <li key={index}>
                    <FontAwesomeIcon
                      icon={faCheck}
                      className={SHARED_ICON_CLASSES}
                    />
                    {note}
                  </li>
                ))}
              </ul>
            </div>
          )
        )}
        <div className="vai-mt-4 vai-text-sm vai-text-zinc-500">
          {claimAttachmentDisclaimer}
        </div>
      </div>
    </div>
  );
};
